/**
 * HtmlBlock content survives the site-builder backend by being stored
 * base64-encoded. The backend HTML-sanitises string block props on save
 * (it strips every tag — `<div><b>x</b></div>` is persisted as `x`), so
 * raw markup can never round-trip. A base64 string contains no angle
 * brackets, so the sanitiser leaves it untouched (verified end-to-end
 * against the backend).
 *
 * The admin editor encodes on save; the portal + admin preview decode
 * before rendering. Values without the {@link HTML_PAYLOAD_PREFIX} are
 * returned as-is, so legacy/plain content keeps working.
 */
export const HTML_PAYLOAD_PREFIX = 'b64:';

/** UTF-8 → base64, isomorphic (Buffer on the server, btoa in the browser). */
function toBase64(input: string): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(input, 'utf8').toString('base64');
  }
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

/** base64 → UTF-8, isomorphic. */
function fromBase64(input: string): string {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(input, 'base64').toString('utf8');
  }
  const binary = atob(input);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/** Encode raw HTML into the backend-safe stored form (`b64:<base64>`). */
export function encodeHtmlPayload(html: string): string {
  const raw = typeof html === 'string' ? html : '';
  if (!raw) return '';
  try {
    return HTML_PAYLOAD_PREFIX + toBase64(raw);
  } catch {
    return raw;
  }
}

/**
 * Decode a stored HtmlBlock value back to raw HTML. Passes through any
 * value that isn't `b64:`-prefixed (legacy plain content, or content the
 * backend already flattened).
 */
export function decodeHtmlPayload(value: unknown): string {
  if (typeof value !== 'string') return '';
  if (!value.startsWith(HTML_PAYLOAD_PREFIX)) return value;
  const encoded = value.slice(HTML_PAYLOAD_PREFIX.length);
  try {
    return fromBase64(encoded);
  } catch {
    return value;
  }
}

/** True when the value is in the encoded (`b64:`) form. */
export function isHtmlPayloadEncoded(value: unknown): boolean {
  return typeof value === 'string' && value.startsWith(HTML_PAYLOAD_PREFIX);
}
